/**
 * GenerateProp Slider — Navigation Controls
 *
 * Progressively enhances every `.generateprop-slider` element on the page
 * with previous/next buttons that use the native CSS scroll snap behaviour.
 *
 * No layout changes are made in the editor; this script only runs on the
 * frontend. The slider itself is fully functional without JavaScript
 * (touch/pointer drag + keyboard arrow keys work natively), so this is
 * purely additive.
 *
 * DOM structure after init:
 *   <div class="generateprop-slider-wrapper">   ← injected wrapper
 *     <div class="generateprop-slider gb-looper-xxx"> ← original element
 *       <div class="gb-loop-item-xxx">...</div>
 *       ...
 *     </div>
 *     <button class="generateprop-slider-btn generateprop-slider-btn--prev">…</button>
 *     <button class="generateprop-slider-btn generateprop-slider-btn--next">…</button>
 *   </div>
 */

(function () {
	'use strict';

	const SLIDER_SELECTOR = '.generateprop-slider';
	const WRAPPER_CLASS = 'generateprop-slider-wrapper';
	const BTN_CLASS = 'generateprop-slider-btn';

	// Matches any GenerateBlocks loop-item regardless of the uniqueId suffix.
	const ITEM_SELECTOR = '[class*="gb-loop-item"]';

	// SVG chevrons — inline so no extra HTTP request and no icon-package dependency.
	const CHEVRON_LEFT =
		'<svg xmlns="http://www.w3.org/2000/svg" width="22" height="16" viewBox="0 0 22 16" fill="none">' +
		'<path d="M0 6.57373H18.7959V8.87996H0V6.57373Z" fill="currentColor"/>' +
		'<path d="M13.5823 15.4549L11.8838 13.8396L18.3117 7.72746L11.8838 1.61533L13.5823 0L21.7063 7.72749L13.5823 15.4549Z" fill="currentColor"/>' +
		'</svg>';

	const CHEVRON_RIGHT =
		'<svg xmlns="http://www.w3.org/2000/svg" width="22" height="16" viewBox="0 0 22 16" fill="none">' +
		'<path d="M0 6.57373H18.7959V8.87996H0V6.57373Z" fill="currentColor"/>' +
		'<path d="M13.5823 15.4549L11.8838 13.8396L18.3117 7.72746L11.8838 1.61533L13.5823 0L21.7063 7.72749L13.5823 15.4549Z" fill="currentColor"/>' +
		'</svg>';

	/**
	 * Return all direct loop-item children of the slider.
	 */
	function getItems(slider) {
		return [...slider.querySelectorAll(ITEM_SELECTOR)];
	}

	/**
	 * Return the index of the leftmost fully-visible item.
	 * "Fully visible" means its left edge is at or right of the slider's left edge.
	 */
	function getCurrentIndex(slider) {
		const items = getItems(slider);
		const sliderLeft = slider.getBoundingClientRect().left;

		for (let i = 0; i < items.length; i++) {
			if (items[i].getBoundingClientRect().left >= sliderLeft - 1) {
				return i;
			}
		}
		return 0;
	}

	/**
	 * Enable/disable prev & next buttons based on current scroll position.
	 */
	function syncButtons(slider, prevBtn, nextBtn) {
		const current = getCurrentIndex(slider);
		// Account for multiple visible items: the slider is at the end when it
		// can't scroll any further right, which happens before the last item
		// becomes the leftmost visible one.
		const maxScroll = slider.scrollWidth - slider.clientWidth;
		prevBtn.disabled = current === 0;
		nextBtn.disabled = slider.scrollLeft >= maxScroll - 1;
	}

	/**
	 * Create a styled navigation button.
	 */
	function createButton(direction) {
		const btn = document.createElement('button');
		btn.type = 'button';
		btn.className = `${BTN_CLASS} ${BTN_CLASS}--${direction}`;
		btn.setAttribute(
			'aria-label',
			direction === 'prev' ? 'Previous slide' : 'Next slide'
		);
		btn.innerHTML = direction === 'prev' ? CHEVRON_LEFT : CHEVRON_RIGHT;
		return btn;
	}

	/**
	 * Initialise a single slider element.
	 */
	function initSlider(slider) {
		const items = getItems(slider);

		// Nothing to navigate if there's only one (or zero) items.
		if (items.length <= 1) {
			return;
		}

		// Wrap the slider so navigation buttons can be positioned absolutely.
		const wrapper = document.createElement('div');
		wrapper.className = WRAPPER_CLASS;
		slider.parentNode.insertBefore(wrapper, slider);
		wrapper.appendChild(slider);

		const prevBtn = createButton('prev');
		const nextBtn = createButton('next');
		wrapper.appendChild(prevBtn);
		wrapper.appendChild(nextBtn);

		prevBtn.addEventListener('click', function () {
			const current = getCurrentIndex(slider);
			if (current > 0) {
				items[current - 1].scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
					inline: 'start',
				});
			}
		});

		nextBtn.addEventListener('click', function () {
			const current = getCurrentIndex(slider);
			if (current < items.length - 1) {
				items[current + 1].scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
					inline: 'start',
				});
			}
		});

		// Keep button states in sync as the user scrolls (native or programmatic).
		slider.addEventListener(
			'scroll',
			function () {
				syncButtons(slider, prevBtn, nextBtn);
			},
			{ passive: true }
		);

		// Set initial disabled state.
		syncButtons(slider, prevBtn, nextBtn);
	}

	/**
	 * Boot — run after DOM is ready.
	 */
	function boot() {
		document.querySelectorAll(SLIDER_SELECTOR).forEach(initSlider);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot);
	} else {
		boot();
	}
})();
